import { Link } from 'react-router-dom';
import type { Video } from '@/types/video';
import { TYPE_LABELS } from '@/types/video';
import { relativeUpdate } from '@/utils/format';

interface VideoCardProps {
  video: Video;
  variant?: 'default' | 'rich';
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} 封面`}
          title={`${video.title} - 91视频在线`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent opacity-80" />
        <span className="font-label absolute left-2.5 top-2.5 inline-flex items-center gap-1 rounded-md bg-background-50/90 px-2 py-1 text-[11px] font-semibold text-primary-700 backdrop-blur">
          <i className="ri-bookmark-2-fill text-accent-500" />
          {TYPE_LABELS[video.type]}
        </span>
        <span className="font-label absolute right-2.5 top-2.5 rounded-md bg-accent-500/95 px-2 py-1 text-[11px] font-bold text-background-50">
          {video.rating.toFixed(1)}
        </span>
        <div className="absolute inset-x-0 bottom-0 p-2.5">
          <span className="font-label inline-flex items-center gap-1 text-[11px] font-medium text-background-100/90">
            <i className="ri-time-line" />
            {relativeUpdate(video.updateDate)}
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500/95 text-background-50">
            <i className="ri-play-fill text-xl" />
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-3">
        <h3 className="font-heading truncate text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
          {video.title}
        </h3>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
          <span>{video.year}</span>
          <span className="h-1 w-1 rounded-full bg-foreground-300" />
          <span>{video.region}</span>
          <span className="h-1 w-1 rounded-full bg-foreground-300" />
          <span className="truncate">{video.genres.join(' · ')}</span>
        </div>
        {variant === 'rich' ? (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{video.description}</p>
        ) : null}
      </div>
    </Link>
  );
}